import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useLanguage } from '../LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import pages from '../pages';
import StorySection from './StorySection';

export default function DesktopStory({ guestDetails }) {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const getPageIndexFromPath = (pathname) => {
    const index = pages.findIndex(
      (p) =>
        p.path === pathname ||
        (p.path === '/' && (pathname === '' || pathname === '/')),
    );
    return index !== -1 ? index : 0;
  };

  const pathIndex = getPageIndexFromPath(location.pathname);
  const [visibleIndex, setVisibleIndex] = useState(pathIndex);

  const [navOpen, setNavOpen] = useState(false);
  const sectionRefs = useRef({});
  const rightPanelRef = useRef(null);
  const leftPanelRef = useRef(null);
  const visibleIndexTimeout = useRef(null);

  useEffect(() => {
    const initialIndex = getPageIndexFromPath(location.pathname);
    if (initialIndex > 0 && sectionRefs.current[initialIndex]) {
      setTimeout(() => {
        sectionRefs.current[initialIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, []);

  const delayedSetVisibleIndex = (index) => {
    if (visibleIndexTimeout.current) {
      clearTimeout(visibleIndexTimeout.current);
    }

    visibleIndexTimeout.current = setTimeout(() => {
      setVisibleIndex(index);
      if (
        pages[index]?.path &&
        window.location.pathname !== pages[index].path
      ) {
        window.history.replaceState(null, '', pages[index].path);
      }
      visibleIndexTimeout.current = null;
    }, 300);
  };

  // Fallback: determine active section by which section contains the vertical midpoint
  useEffect(() => {
    const el = rightPanelRef.current;
    if (!el) return;

    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const mid = el.scrollTop + el.clientHeight / 2;
        const refs = sectionRefs.current || {};
        let found = null;
        Object.keys(refs).forEach((k) => {
          const node = refs[k];
          if (!node) return;
          const offset = node.offsetTop;
          const height = node.offsetHeight;
          if (mid >= offset && mid < offset + height) found = Number(k);
        });
        if (found !== null) {
          delayedSetVisibleIndex(found);
        }
        ticking = false;
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    // initialize
    onScroll();

    return () => el.removeEventListener('scroll', onScroll);
  }, [delayedSetVisibleIndex]);

  const handleLeftScroll = (e) => {
    if (rightPanelRef.current && e.deltaY !== 0) {
      rightPanelRef.current.scrollTop += e.deltaY;
    }
  };

  return (
    <>
      <div className="relative">
        {/* NAVIGATION BAR */}
        <motion.div
          initial={{ x: -300 }}
          animate={{ x: navOpen ? 0 : -300 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="nav-sidebar fixed left-0 top-0 h-screen w-72 shadow-lg z-50 overflow-y-auto"
        >
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-8 text-wedding-heading font-title">
              Daniel & Tracer
            </h2>
            <nav className="space-y-2">
              {pages.map((page, index) => (
                <motion.button
                  key={page.id}
                  onClick={() => {
                    delayedSetVisibleIndex(index);
                    setNavOpen(false);
                    navigate(page.path);
                    sectionRefs.current[index]?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }}
                  whileHover={{ x: 10 }}
                  className={
                    visibleIndex === index
                      ? 'nav-sidebar-item-active'
                      : 'nav-sidebar-item'
                  }
                >
                  {t('nav.' + page.key) || page.title}
                </motion.button>
              ))}
            </nav>
          </div>
        </motion.div>

        {/* OVERLAY */}
        {navOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setNavOpen(false)}
            className="fixed inset-0 z-40 bg-heading/30"
          />
        )}

        {/* MAIN CONTENT */}
        <div className="grid grid-cols-[65%_35%]">
          {/* TOGGLE BUTTON */}
          <motion.button
            onClick={() => setNavOpen(!navOpen)}
            className="btn-nav-toggle fixed top-6 left-6 z-10"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg
              className="w-6 h-6 text-wedding-nav"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </motion.button>

          {/* LANGUAGE TOGGLE */}
          <LanguageToggle className="btn-language-toggle-fixed top-6 right-8" />

          {/* LEFT IMAGE */}
          <div
            className="sticky top-0 h-screen overflow-hidden relative"
            ref={leftPanelRef}
            onWheel={handleLeftScroll}
          >
            {pages.map((page, index) => (
              <div
                key={page.id}
                className="absolute inset-0 flex items-center justify-center"
              >
                <motion.img
                  src={page.image}
                  className="w-full h-full object-cover will-change-opacity"
                  animate={{
                    opacity: visibleIndex === index ? 1 : 0,
                  }}
                  transition={{
                    duration: 1.5,
                    ease: 'easeInOut',
                  }}
                  loading="eager"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent" />

                <div className="absolute inset-0 pointer-events-none flex items-end bottom-[3%] left-[3%]">
                  <div className="m-8 p-6 text-white max-w-xl items-end">
                    <h2 className="text-7xl text-banner-title">
                      Daniel & Tracer
                    </h2>
                    <p className="mt-2 text-4xl opacity-80 text-banner-subtitle">
                      {t('main.welcome_message_1')}
                    </p>
                    <p className="mt-2 text-4xl opacity-80 text-banner-subtitle">
                      {t('main.welcome_message_2')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT TEXT */}
          <div
            className="overflow-y-auto h-screen bg-wedding-blush"
            ref={rightPanelRef}
          >
            {pages.map((page, index) => {
              const Component = page.desktopComponent || page.component;
              if (Component) {
                return (
                  <div
                    key={page.id}
                    ref={(el) => {
                      sectionRefs.current[index] = el;
                    }}
                  >
                    <StorySection
                      index={index}
                      setActive={delayedSetVisibleIndex}
                      component={Component}
                      title={index !== 0 ? t(page.titleKey) : undefined}
                      subtitle={
                        page.subtitleKey ? t(page.subtitleKey) : undefined
                      }
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
    </>
  );
}
